/**
 * Celebrations — opt-in toggle (local UI preference; no persistence
 * endpoint was provided for it), this-week list with Wish action.
 * Wired: GET /api/discussions/celebrations. Port of BirthdaysScreen in
 * design/source/screens-vendor-issue.jsx.
 */
import React, { useState } from 'react';
import { View } from 'react-native';

import { radius, space } from '@bero/tokens';
import { Icon } from '@/components/gate/icons';
import { ArchAvatar, goBack, TogglePill } from '@/features/community/bits';
import { fetchCelebrations, useFetch, wishCelebration, withWish, celebrationKindLabel } from '@/features/community/lib';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, Button, EmptyState, Hairline, Screen, SectionLabel, Skeleton, TopBar } from '@/ui';

export default function BirthdaysScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { data, loading, error, refetch, mutate } = useFetch(fetchCelebrations, []);
  const [shareOptIn, setShareOptIn] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const list = data ?? [];

  const toggleWish = async (id: string) => {
    if (busyId) return;
    const prev = data;
    setBusyId(id);
    mutate((cs) => (cs ?? []).map((c) => (c.id === id ? withWish(c) : c)));
    try {
      await wishCelebration(id);
    } catch {
      mutate(() => prev);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
      <TopBar title={t('ml_celebrations')} onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        <View style={{
          backgroundColor: colors.canvas1, borderRadius: radius.lg, padding: space.s4,
          flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: space.s4,
        }}>
          <View style={{ flex: 1, paddingRight: space.s3 }}>
            <AppText variant="bodyMd" weight="500">{t('bd_share')}</AppText>
            <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
              {t('bd_share_sub')}
            </AppText>
          </View>
          <TogglePill value={shareOptIn} onChange={setShareOptIn} label={t('bd_share')} />
        </View>

        {loading ? (
          <View style={{ gap: space.s3 }}>
            {[0, 1, 2].map((i) => <Skeleton key={i} h={72} />)}
          </View>
        ) : error ? (
          <EmptyState
            title={t('bd_err_title')}
            body={t('bd_err_body')}
            cta={t('retry')}
            onCta={() => void refetch()}
          />
        ) : list.length === 0 ? (
          <EmptyState
            icon={<Icon name="calendar" size={40} color={colors.textTertiary} />}
            title={t('bd_empty_title')}
            body={t('bd_empty_body')}
          />
        ) : (
          <>
            <SectionLabel>{t('bd_this_week')}</SectionLabel>
            <View>
              {list.map((c, i) => (
                <View key={c.id}>
                <View
                  style={{
                    flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingVertical: space.s4,
                    ...(i === 0 ? { backgroundColor: colors.accent100, borderRadius: radius.lg, paddingHorizontal: space.s3 } : {}),
                  }}
                >
                  <ArchAvatar name={c.name || '?'} size={44} tone={i === 0 ? 'accent' : 'primary'} />
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <AppText variant="bodyMd" weight="500">{c.name}</AppText>
                    <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                      {celebrationKindLabel(c.kind, t)}{c.flat ? ` · ${c.flat}` : ''}{c.date ? ` · ${c.date}` : ''}
                      {c.wishCount > 0 ? ` · ${t('bd_wished_count', { n: c.wishCount })}` : ''}
                    </AppText>
                  </View>
                  <Button
                    title={c.wished ? t('bd_wished') : t('bd_wish')}
                    kind="secondary"
                    loading={busyId === c.id}
                    style={{ paddingVertical: space.s2, paddingHorizontal: space.s3, minHeight: 40 }}
                    onPress={() => void toggleWish(c.id)}
                  />
                </View>
                {i < list.length - 1 && i !== 0 ? <Hairline /> : null}
                </View>
              ))}
            </View>
          </>
        )}
      </Screen>
    </View>
  );
}
